/**
 * vodStreamsCache — in-memory cache for xtream getMovies()
 *
 * Avoids re-fetching the full VOD list (can be thousands of items)
 * for every page that needs VOD matching. TTL is 4 hours; concurrent callers
 * share a single in-flight promise so only one network request is issued.
 *
 * Reset automatically when xtreamAPI credentials change (just call
 * invalidateVodStreamsCache() from setCredentials call sites).
 */

import xtreamAPI, { XtreamContent } from '../api/xtreamAPI';

const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

let cachedStreams: XtreamContent[] | null = null;
let cacheAtMs = 0;
let inflightPromise: Promise<XtreamContent[]> | null = null;

export async function getCachedVodStreams(): Promise<XtreamContent[]> {
  if (cachedStreams && Date.now() - cacheAtMs < CACHE_TTL_MS) {
    return cachedStreams;
  }
  // Deduplicate concurrent callers
  if (inflightPromise) return inflightPromise;

  inflightPromise = xtreamAPI.getMovies().then((streams) => {
    cachedStreams = streams;
    cacheAtMs = Date.now();
    inflightPromise = null;
    return streams;
  }).catch((err) => {
    inflightPromise = null;
    throw err;
  });

  return inflightPromise;
}

export function invalidateVodStreamsCache(): void {
  cachedStreams = null;
  cacheAtMs = 0;
  inflightPromise = null;
}
